/**
 * Raiz de la aplicacion: decide entre pantalla de acceso y el sistema.
 */

import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { Lock } from 'lucide-react';
import type { Permission } from '@abarrotes/shared';
import { useSession } from './lib/session.js';
import { useRouter } from './lib/router.js';
import { useBrandingWhenReady } from './lib/branding.js';
import { Shell, ROUTE_PERMISSION } from './components/Shell.js';
import { LoginScreen } from './features/auth/LoginScreen.js';
import { DemoScreen } from './features/demo/DemoScreen.js';
import { FEATURES } from './lib/features.js';
import { BrandLogo } from './components/BrandLogo.js';
import { Installer } from './components/Installer.js';
import { PermissionsTour } from './components/PermissionsTour.js';
import { Button, EmptyState } from './components/ui.js';

import { SeedRain } from './components/SeedRain.js';
import { ROUTES } from './routes.js';

/*
 * Las ocho pantallas del sistema se piden cuando se entra a ellas, no al abrir.
 *
 * Antes se importaban todas de golpe y el resultado era un solo archivo de 944
 * kB: quien abria la caja pagaba tambien por la inteligencia de surtido, el
 * credito y el inventario. En la tienda eso se nota, porque el sistema se abre
 * sobre el WiFi del changarro y a veces sobre datos del celular.
 *
 * `LoginScreen` y `DemoScreen` NO van aqui, a proposito. Son el primer cuadro
 * que ve alguien —una es la puerta del sistema y la otra es el enlace que se
 * comparte para enseñarlo—, y partirlas cambiaria ese primer cuadro por la
 * pantalla de carga durante un viaje de red. Ahorrar unos kilobytes no vale
 * hacer dudar al que apenas esta llegando.
 *
 * `lazy` quiere una exportacion por omision y aqui todas son con nombre; de ahi
 * el `.then` que la envuelve.
 */
const PosScreen = lazy(() => import('./features/pos/PosScreen.js').then((m) => ({ default: m.PosScreen })));
const DashboardScreen = lazy(() => import('./features/dashboard/DashboardScreen.js').then((m) => ({ default: m.DashboardScreen })));
const IntelligenceScreen = lazy(() => import('./features/intelligence/IntelligenceScreen.js').then((m) => ({ default: m.IntelligenceScreen })));
const CreditScreen = lazy(() => import('./features/credit/CreditScreen.js').then((m) => ({ default: m.CreditScreen })));
const InventoryScreen = lazy(() => import('./features/inventory/InventoryScreen.js').then((m) => ({ default: m.InventoryScreen })));
const ExpensesScreen = lazy(() => import('./features/expenses/ExpensesScreen.js').then((m) => ({ default: m.ExpensesScreen })));
const CashScreen = lazy(() => import('./features/cash/CashScreen.js').then((m) => ({ default: m.CashScreen })));
const SalesScreen = lazy(() => import('./features/sales/SalesScreen.js').then((m) => ({ default: m.SalesScreen })));
const StaffScreen = lazy(() => import('./features/staff/StaffScreen.js').then((m) => ({ default: m.StaffScreen })));
const StoresScreen = lazy(() => import('./features/stores/StoresScreen.js').then((m) => ({ default: m.StoresScreen })));
const SettingsScreen = lazy(() => import('./features/settings/SettingsScreen.js').then((m) => ({ default: m.SettingsScreen })));

export function App(): ReactNode {
  const { user, isLoading, storeId, sessionError, reloadSession, can } = useSession();
  const { path } = useRouter();
  const { branding, ready } = useBrandingWhenReady();

  /*
   * El titulo de la pestaña lleva el nombre corto del negocio.
   *
   * Se escribe aqui, en tiempo de ejecucion, y no en el <title> del HTML: la
   * identidad viene del servidor a proposito, para que instalar el sistema en
   * otra tienda sea cambiar el .env y no recompilar. Y se espera a que el
   * nombre este listo por la misma razon que en la pantalla de acceso: escribir
   * el generico y corregirlo medio segundo despues se ve como que el sistema
   * duda de como se llama el negocio.
   */
  useEffect(() => {
    if (ready) document.title = branding.shortName;
  }, [ready, branding.shortName]);

  // El recorrido de demostracion va antes que cualquier compuerta de sesion:
  // es publico a proposito —existe para enseñar el sistema antes de tener
  // cuenta— y no toca datos, asi que ni siquiera espera a saber quien eres.
  //
  // Con el recorrido apagado (`lib/features.ts`) `/demo` sigue el camino de
  // cualquier direccion: acceso si no hay sesion, Caja si la hay.
  if (FEATURES.demo && path === ROUTES.demo) return <DemoScreen />;

  if (isLoading) return <Splash />;

  /*
   * "No pude preguntar quien eres" NO es "no has entrado".
   *
   * Debajo, la falta de usuario manda a la pantalla de acceso. Si un tropiezo
   * de red cayera en esa misma rama, el cajero veria pedir la contraseña a
   * media venta —con el carrito lleno y el cliente enfrente— por algo que no
   * tiene que ver con su sesion.
   */
  if (!user && sessionError) {
    return (
      <Splash>
        <p className="text-ink font-medium">No se pudo cargar tu sesion</p>
        <p className="text-muted text-sm mt-1 text-pretty max-w-sm">
          No es que hayas salido: no se pudo preguntar. Revisa la conexion y vuelve a intentar.
        </p>
        <p className="text-subtle text-xs mt-2 max-w-sm [overflow-wrap:anywhere]">
          {sessionError.message}
        </p>
        <div className="mt-4">
          <Button variant="secondary" onClick={reloadSession}>
            Reintentar
          </Button>
        </div>
      </Splash>
    );
  }

  /*
   * El instalador va tambien en la pantalla de acceso, no solo en el marco: en
   * iPhone conviene instalar ANTES de entrar, porque la app instalada no comparte
   * la sesion de Safari y entrar primero ahi obliga a entrar dos veces. Ver
   * `components/Installer.tsx`.
   */
  if (!user) {
    return (
      <>
        <LoginScreen />
        <Installer />
      </>
    );
  }

  if (user.stores.length === 0) {
    return (
      <Splash>
        <p className="text-ink font-medium">Tu cuenta no tiene ninguna sucursal asignada.</p>
        <p className="text-muted text-sm mt-1">Pidele al dueño que te de acceso a una tienda.</p>
      </Splash>
    );
  }

  // El efecto que elige sucursal corre justo despues del primer render; este
  // caso dura un cuadro y evita que las pantallas tengan que manejar el nulo.
  if (storeId === null) return <Splash />;

  /*
   * El `Suspense` va DENTRO del marco, no envolviendolo.
   *
   * Asi la barra lateral, el encabezado y la barra inferior se quedan quietos
   * mientras llega el trozo de la pantalla nueva, y lo unico que cambia es el
   * area que se desplaza. Envolviendo el `Shell` entero, cada cambio de
   * pantalla parpadearia el marco completo.
   */
  return (
    <>
      <Shell>
        <Suspense fallback={<ScreenFallback />}>{renderRoute(path, can)}</Suspense>
      </Shell>
      <Installer />
      {/* Los permisos de la primera vez, uno por uno; espera su turno con el
          instalador. Va aqui y no en la pantalla de acceso: encender los avisos
          guarda el aparato a nombre de la sesion, y antes de entrar no hay. */}
      <PermissionsTour />
    </>
  );
}

/**
 * Relleno mientras llega el trozo de una pantalla.
 *
 * Ocupa el alto disponible sin empujar nada: `h-full` sobre el area que ya se
 * desplaza, y `min-h-0` no hace falta aqui porque no lleva lista adentro.
 * Dura lo que un viaje de red en la misma sesion, asi que no lleva marca ni
 * texto: enseñar el logotipo por doscientos milisegundos cada vez que se
 * cambia de pantalla se lee como que el sistema se reinicia.
 */
function ScreenFallback(): ReactNode {
  return (
    <div className="h-full grid place-items-center py-16">
      <span aria-hidden className="block h-0.5 w-28 rounded-full bg-surface-2 overflow-hidden">
        <span className="block h-full w-1/3 rounded-full bg-primary animate-loading-sweep" />
      </span>
      <span className="sr-only">Cargando</span>
    </div>
  );
}

function renderRoute(path: string, can: (permission: Permission) => boolean): ReactNode {
  /*
   * El candado por direccion. Esconder el renglon del menu no basta: la barra de
   * direcciones existe, y una pantalla que se monta y falla campo por campo se
   * lee como un sistema roto, no como una puerta cerrada.
   *
   * Esto NO es control de acceso —eso lo aplican la RLS y las RPC, que revalidan
   * el permiso adentro— es lo que hace que el sistema diga la verdad.
   *
   * La direccion desconocida cae en la Caja, asi que hereda el permiso de la
   * Caja: si no, quedaria un hueco por donde entrar sin comprobar nada.
   *
   * `null` es otra cosa que "desconocida": es una pantalla que existe y no pide
   * permiso (Configuracion, que es del aparato y no del negocio). Por eso se
   * pregunta si la direccion ESTA, y no se cae al de la Caja con `??`: con `??`
   * el `null` heredaria `sale:create` sin que nadie lo decidiera.
   */
  const required = Object.hasOwn(ROUTE_PERMISSION, path) ? ROUTE_PERMISSION[path] : ROUTE_PERMISSION[ROUTES.pos];
  if (required !== undefined && required !== null && !can(required)) return <NoAccess />;

  switch (path) {
    case ROUTES.dashboard:
      return <DashboardScreen />;
    case ROUTES.intelligence:
      return <IntelligenceScreen />;
    case ROUTES.credit:
      // Apagado, el renglon no existe y la ruta hereda el permiso de la Caja:
      // sin esta guarda cualquiera que cobra abriria la cartera tecleando la
      // direccion.
      return FEATURES.credit ? <CreditScreen /> : <PosScreen />;
    case ROUTES.inventory:
      return <InventoryScreen />;
    case ROUTES.expenses:
      return <ExpensesScreen />;
    case ROUTES.cash:
      return <CashScreen />;
    case ROUTES.sales:
      return <SalesScreen />;
    case ROUTES.staff:
      return <StaffScreen />;
    case ROUTES.stores:
      return <StoresScreen />;
    case ROUTES.settings:
      return <SettingsScreen />;
    case ROUTES.pos:
      return <PosScreen />;
    default:
      // Cualquier direccion desconocida cae en la caja, que es la pantalla que
      // el negocio necesita disponible siempre.
      return <PosScreen />;
  }
}

/**
 * Esta pantalla no es para este usuario.
 *
 * Dice que el sistema esta bien y que la direccion no le toca, y SIEMPRE ofrece
 * la salida: quien llega aqui casi siempre lo hizo por un enlace viejo o por un
 * dedazo, y dejarlo mirando un aviso sin boton es dejarlo atorado. La Caja es el
 * destino porque es la unica pantalla que todo el mundo con cuenta puede abrir.
 *
 * No dice QUE permiso falta. Al que atiende el mostrador no le sirve leer
 * `report:operations`, y a quien quisiera hurgar tampoco hay que darle el mapa.
 */
function NoAccess(): ReactNode {
  // `navigate` y no un `<Link>` envolviendo el boton: un `<button>` dentro de un
  // `<a>` es contenido interactivo anidado, que ningun navegador tiene que
  // resolver igual y los lectores de pantalla anuncian dos veces.
  const { navigate } = useRouter();

  return (
    <EmptyState
      icon={<Lock className="size-8" />}
      title="Esta pantalla no es para tu usuario"
      description="Tu cuenta no tiene acceso aqui. Si crees que deberia tenerlo, pideselo a la dueña."
      action={
        <Button variant="primary" onClick={() => navigate(ROUTES.pos)}>
          Ir a la Caja
        </Button>
      }
    />
  );
}

/**
 * Pantalla de carga.
 *
 * Dura fracciones de segundo con la base local, asi que NO se anima la entrada:
 * un logotipo que aparece con recorrido y desaparece antes de terminarlo se ve
 * peor que uno que simplemente esta ahi. Lo unico que se mueve es la barra de
 * avance, que comunica "esto sigue trabajando".
 *
 * Se muestra la marca en lugar de una rueda pelada porque es el primer cuadro
 * que ve el usuario, y una rueda gris no dice de quien es el sistema.
 */
function Splash({ children }: { children?: ReactNode }): ReactNode {
  return (
    <div className="relative h-app grid place-items-center bg-bg px-6 overflow-hidden">
      {/*
        Media lluvia. La pantalla dura fracciones de segundo con la base local,
        asi que doce siluetas no alcanzan a distinguirse de seis: seria pagar
        elementos que nadie ve. Cuando la carga se alarga —una tienda con la
        base grande, un respaldo corriendo— entonces si tiene con que llenarse.
      */}
      <SeedRain variant="soft" />

      <div className="relative flex flex-col items-center text-center animate-in-fade">
        {children ?? (
          <>
            <span className="inline-flex rounded-[23.4%] shadow-card">
              <BrandLogo className="size-14" />
            </span>

            {/* Barra indeterminada: un destello que recorre el riel. */}
            <span
              aria-hidden
              className="mt-6 block h-0.5 w-28 rounded-full bg-surface-2 overflow-hidden"
            >
              <span className="block h-full w-1/3 rounded-full bg-primary animate-loading-sweep" />
            </span>

            <span className="sr-only">Cargando</span>
          </>
        )}
      </div>
    </div>
  );
}
